import {
  Meetings,
  IMeeting,
  IMeetings,
  Member,
  tSharedAppState,
  adjustSeatsUsed
} from 'common/modules/ReactShared';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
// Direct sibling import (not via the barrel) so the action creators are fully
// initialized when this slice's extraReducers builder runs at module load.
import { actions as meetingActions } from '../meeting/slice';

const initialState: IMeetings = {
  meetings: [],
  currentPage: 1,
  totalPages: 0,
  nextPage: null,
  prevPage: null,
  limitValue: 25,
  totalCount: 0
};

const getMeetings = createAsyncThunk<
  Meetings.MeetingsApiTypes.IMeetings,
  { searchValue?: string; startTime?: string; endTime?: string; perPage?: string }
>(
  'meetings/getMeetings_request',
  async ({ searchValue, startTime, endTime, perPage }) => {
    const data = await Meetings.meetingsApi.getMeetings({
      searchValue,
      startTime,
      endTime,
      perPage
    });
    return data;
  }
);

const getMeetingsMore = createAsyncThunk<
  Meetings.MeetingsApiTypes.IMeetings | null,
  string | undefined,
  { state: tSharedAppState }
>('meetings/getMeetingsMore_request', async (searchValue, { getState }) => {
  const pageNumber = Member.selectors.selectMeetingsStore(getState())?.nextPage;
  if (!pageNumber) {
    return null;
  }

  const data = await Meetings.meetingsApi.getMeetings({ page: pageNumber, searchValue });
  return data;
});

const meetingsSlice = createSlice({
  name: 'meetings',
  initialState: initialState as IMeetings,
  reducers: {},

  extraReducers: (builder) => {
    builder.addCase(getMeetings.fulfilled, (_, action) => {
      return replaceZeroLevelInMeetings(action.payload);
    });

    builder.addCase(getMeetingsMore.fulfilled, (state, action) => {
      const { payload } = action;
      if (payload) {
        return {
          ...payload,
          meetings: [
            ...replaceZeroLevelInMeetings(state).meetings,
            ...replaceZeroLevelInMeetings(payload).meetings
          ]
        };
      }
      return state;
    });

    // The reservation thunks live in the single-meeting slice; mirror their
    // result here so a booking made on one screen is immediately visible on
    // the list screens without a refetch.
    builder.addCase(meetingActions.createReservation.fulfilled, (state, action) => {
      applyReservationChange(state, action.meta.arg, true, action.payload);
    });

    builder.addCase(meetingActions.removeReservation.fulfilled, (state, action) => {
      applyReservationChange(state, action.meta.arg, false, action.payload);
    });
  }
});

const { reducer } = meetingsSlice;

const actions = {
  getMeetings,
  getMeetingsMore
};

export { actions, reducer };

// Hoisted so it is available when createSlice runs the extraReducers builder
// at module load.
function applyReservationChange(
  state: IMeetings,
  meetingId: number,
  reserved: boolean,
  serverMeeting: Pick<IMeeting, 'seatsUsed' | 'seatsLimit'> | undefined
): void {
  const meeting = state.meetings.find((item) => item.id === meetingId);
  if (!meeting) return;

  // A refetch may have already landed the target state (with server-accurate
  // seat counts) while the thunk was in flight; re-applying the +/-1 then
  // would drift the count. Only adjust when the flag actually flips.
  const reservationChanged = meeting.reservation !== reserved;

  meeting.reservation = reserved;

  // Server truth wins when the endpoint returns seat counts; otherwise apply
  // the same optimistic +/-1 the cards use.
  if (typeof serverMeeting?.seatsUsed === 'number') {
    meeting.seatsUsed = serverMeeting.seatsUsed;
  } else if (reservationChanged && typeof meeting.seatsUsed === 'number') {
    meeting.seatsUsed = adjustSeatsUsed(
      meeting.seatsUsed,
      meeting.seatsLimit,
      reserved ? 1 : -1
    );
  }
}

function replaceZeroLevelInMeetings(
  meetings: Meetings.MeetingsApiTypes.IMeetings
): IMeetings {
  return {
    ...meetings,
    meetings: meetings.meetings.map((meeting) => ({
      ...meeting,
      level: meeting.level === 'zero_level' ? 'a1' : meeting.level
    }))
  };
}
